import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { LocaleService } from './locale';
import { fetchAuthSession } from 'aws-amplify/auth';
import { environment } from 'src/environments/environment';

export type EmailType =
  | 'welcome'
  | 'subscription_purchase'
  | 'subscription_cancel'
  | 'invite_send'
  | 'invite_accepted'
  | 'invite_rejected'
  | 'forgot_password'
  | 'course_director_notification'
  | 'enrollment_notification'
  | 'cfpro_activation';

export type EmailLanguage = 'english' | 'italian';

export interface WelcomeEmailData {
  userName?: string;
  loginLink?: string;
}

export interface SubscriptionPurchaseEmailData {
  planName: string;
  amount: string;
  currency?: string;
  billingPeriod?: string;
  nextBillingDate: string;
}

export interface CfproActivationEmailData {
  firstName: string;
  lastName: string;
  planName: string;
}

export interface EnrollmentNotificationData {
  firstName: string;
  lastName: string;
  userEmail: string;
  planName: string;
}

export interface SubscriptionCancelEmailData {
  planName: string;
  endDate: string;
}

export interface InviteSendEmailData {
  companyName: string;
  inviteLink: string;
}

export interface InviteResponseEmailData {
  companyName: string;
  inviteeEmail: string;
}

export interface ForgotPasswordEmailData {
  resetLink: string;
  expiryTime?: string;
}

export interface CourseDirectorNotificationData {
  companyName: string;
  vatNumber: string;
  contactEmail: string;
  contactPhone: string;
  planName: string;
  purchaseDateTime: string;
  orderId: string;
  subscriptionId: string;
}

type EmailData =
  | WelcomeEmailData
  | SubscriptionPurchaseEmailData
  | SubscriptionCancelEmailData
  | InviteSendEmailData
  | InviteResponseEmailData
  | ForgotPasswordEmailData
  | CourseDirectorNotificationData
  | EnrollmentNotificationData
  | CfproActivationEmailData;

@Injectable({
  providedIn: 'root'
})
export class EmailService {
  private emailEndpoint = environment.lambdaUrls.email;
  private localeService = inject(LocaleService);

  constructor(private http: HttpClient) { }

  private getLanguage(): EmailLanguage {
    return this.localeService.getLanguage() as EmailLanguage;
  }

  /**
   * Send a welcome email when user signs up
   * @returns true if email sent successfully, false otherwise
   */
  async sendWelcomeEmail(to: string, data: WelcomeEmailData): Promise<boolean> {
    return this.sendEmail('welcome', to, data);
  }

  /**
   * Send subscription purchase confirmation email
   * @returns true if email sent successfully, false otherwise
   */
  async sendSubscriptionPurchaseEmail(to: string, data: SubscriptionPurchaseEmailData): Promise<boolean> {
    return this.sendEmail('subscription_purchase', to, data);
  }

  /**
   * Send subscription cancellation email
   * @returns true if email sent successfully, false otherwise
   */
  async sendSubscriptionCancelEmail(to: string, data: SubscriptionCancelEmailData): Promise<boolean> {
    return this.sendEmail('subscription_cancel', to, data);
  }

  /**
   * Send invite email to invitee
   * @returns true if email sent successfully, false otherwise
   */
  async sendInviteEmail(to: string, data: InviteSendEmailData): Promise<boolean> {
    return this.sendEmail('invite_send', to, data);
  }

  /**
   * Send notification when invite is accepted
   * @returns true if email sent successfully, false otherwise
   */
  async sendInviteAcceptedEmail(to: string, data: InviteResponseEmailData): Promise<boolean> {
    return this.sendEmail('invite_accepted', to, data);
  }

  /**
   * Send notification when invite is rejected
   * @returns true if email sent successfully, false otherwise
   */
  async sendInviteRejectedEmail(to: string, data: InviteResponseEmailData): Promise<boolean> {
    return this.sendEmail('invite_rejected', to, data);
  }

  /**
   * Send forgot password email with reset link
   * @returns true if email sent successfully, false otherwise
   */
  async sendForgotPasswordEmail(to: string, data: ForgotPasswordEmailData): Promise<boolean> {
    return this.sendEmail('forgot_password', to, data);
  }

  /**
   * Send course director notification when Pro/Master plan is purchased
   * @returns true if email sent successfully, false otherwise
   */
  async sendCourseDirectorNotification(data: CourseDirectorNotificationData): Promise<boolean> {
    return this.sendEmail('course_director_notification', environment.courseDirectorEmail, data);
    // return this.sendEmail('course_director_notification', "carbenfootprinttest@yopmail.com", data);
  }

  /**
   * Send enrollment notification to Forever Bambù for course registration
   * @returns true if email sent successfully, false otherwise
   */
  async sendEnrollmentNotification(data: EnrollmentNotificationData): Promise<boolean> {
    return this.sendEmail('enrollment_notification', environment.enrollmentEmail, data);
    // return this.sendEmail('enrollment_notification', "carbenfootprinttest@yopmail.com", data);
  }

  /**
   * Send CFPro activation email with training course info to customer
   * @returns true if email sent successfully, false otherwise
   */
  async sendCfproActivationEmail(to: string, data: CfproActivationEmailData): Promise<boolean> {
    return this.sendEmail('cfpro_activation', to, data);
  }

  /**
   * Check if a user with the given email exists in Cognito user pool
   */
  async checkUserExists(email: string): Promise<boolean> {
    try {
      const authToken = await this.getAuthToken();
      if (!authToken) return false;

      const headers = new HttpHeaders({
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${authToken}`
      });

      const response = await firstValueFrom(
        this.http.post<{ exists?: boolean }>(this.emailEndpoint, {
          action: 'check_user_exists',
          email: email.trim().toLowerCase()
        }, { headers })
      );

      return response?.exists === true;
    } catch (error) {
      console.error('Error checking user existence:', error);
      return false;
    }
  }

  /**
   * Get Cognito ID token for authentication
   */
  private async getAuthToken(): Promise<string | null> {
    try {
      const session = await fetchAuthSession();
      return session.tokens?.idToken?.toString() || null;
    } catch (error) {
      console.error('Error getting auth token:', error);
      return null;
    }
  }

  /**
   * Generic email sending method
   * Note: This method catches errors internally and does not throw,
   * allowing the application to continue even if email sending fails.
   */
  private async sendEmail(emailType: EmailType, to: string, data: EmailData): Promise<boolean> {
    try {
      const url = this.emailEndpoint;
      const language = this.getLanguage();
      const body = {
        emailType,
        to,
        data,
        language
      };

      // Get Cognito auth token
      const authToken = await this.getAuthToken();
      if (!authToken) {
        console.warn(`Cannot send ${emailType} email: Not authenticated`);
        return false;
      }

      // Include Cognito token for authentication
      const headers = new HttpHeaders({
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${authToken}`
      });

      await firstValueFrom(
        this.http.post<{ message?: string }>(url, body, { headers })
      );

      console.log(`${emailType} email sent successfully`);
      return true;
    } catch (error: any) {
      // Log error but don't throw - allow application to continue
      console.error(`Error sending ${emailType} email:`, error?.status || error?.message);
      return false;
    }
  }
}
